import React, { useState } from "react";
import cx from "classnames";
import { motion, AnimatePresence } from "motion/react";
import { useToggle } from "usehooks-ts";
import { MediaType } from "shared-types";
import { getMediaDuration } from "shared-utils";
import { useTranslation } from "i18n";
import { ImageWithFallback } from "component-library";
import { PlayIcon, VolumeHighIcon, MicIcon, CameraIcon } from "assets";
import { EDIT_AUDIO_FULL_PATH, EDIT_VIDEO_FULL_PATH } from "shared/constants";
import ProfileCard from "pages/Profile/components/common/ProfileCard";
import { useGetMedia } from "pages/Profile/hooks/useGetMedia";
import SectionControl from "pages/Profile/components/SectionControl";
import MediaViewer from "pages/Profile/components/MediaSection/components/MediaViewer";
import EditButton from "pages/Profile/components/common/EditButton";
import ProfileButton from "pages/Profile/components/common/ProfileButton";
import MediaPlayer, {
  PlayerControls,
} from "pages/Profile/components/common/MediaPlayer/MediaPlayer";

interface Props {
  isEdit?: boolean;
  bodyExpanded?: boolean;
  clickBodyToggle: () => void;
}

const iconStyles =
  "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 drop-shadow-icon text-white";
const DISPLAY_MEDIA_LIMIT = 6;

const MediaSection = ({ isEdit, bodyExpanded, clickBodyToggle }: Props) => {
  const { t } = useTranslation();
  const mediaPlayerRef = React.useRef<PlayerControls>(null);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
  const [isExpanded, isExpandedToggle] = useToggle();
  const [fullScreenViewerOpen, toggleFullScreenViewer] = useToggle();
  const [isEditOpen, toggleIsEditOpen] = useToggle(false);

  const { media = [], isLoading } = useGetMedia();

  const hasHiddenMedia = media.length > DISPLAY_MEDIA_LIMIT;
  const selectedMedia = media[selectedMediaIndex];

  return (
    <div className="grid grid-cols-1 gap-6">
      <SectionControl
        isExpanded={bodyExpanded}
        toggleClick={clickBodyToggle}
        fullScreenClick={() => {
          toggleFullScreenViewer();

          if (!fullScreenViewerOpen) {
            mediaPlayerRef.current?.media?.pause();
          }
        }}
        className="right-3 top-3"
      />
      <ProfileCard className="flex p-0" isLoading={isLoading}>
        <MediaPlayer
          src={selectedMedia?.src}
          poster={selectedMedia?.poster}
          title={selectedMedia?.title}
          type={selectedMedia?.mediaType}
          ref={mediaPlayerRef}
        />
        {isEdit && (
          <div className="absolute right-3 top-14 z-10 flex flex-col items-end gap-1.5">
            <EditButton
              onClick={toggleIsEditOpen}
              className="text-white"
              iconClassName="drop-shadow-icon"
            />
            <div
              className={cx(
                "flex origin-top flex-col items-end gap-1.5 transition-all duration-300",
                isEditOpen
                  ? "scale-100 opacity-100"
                  : "pointer-events-none scale-95 opacity-0"
              )}
            >
              <ProfileButton
                to={EDIT_AUDIO_FULL_PATH}
                isText
                className="rounded-full bg-blue-han/80 font-medium normal-case text-white hover:bg-blue-han"
              >
                <MicIcon width={12} height={12} />
                <span className="drop-shadow-icon">
                  {t("common:button.label.edit.audio")}
                </span>
              </ProfileButton>
              <ProfileButton
                to={EDIT_VIDEO_FULL_PATH}
                isText
                className="rounded-full bg-blue-han/80 font-medium normal-case text-white hover:bg-blue-han"
              >
                <CameraIcon width={12} height={12} />
                <span className="drop-shadow-icon">
                  {t("common:button.label.edit.video")}
                </span>
              </ProfileButton>
            </div>
          </div>
        )}
      </ProfileCard>
      <div className="grid grid-cols-2 gap-3">
        <AnimatePresence>
          {media.map((media, idx) => {
            const isSelected = idx === selectedMediaIndex;

            if (idx > DISPLAY_MEDIA_LIMIT - 1 && !isExpanded) return null;

            return (
              <motion.button
                layout
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.95 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                key={`card-${media.id}-${idx}`}
                data-testid={`media-select-${media.id}`}
                onClick={() => setSelectedMediaIndex(idx)}
                disabled={media.isProcessing}
              >
                <ProfileCard
                  className={cx(
                    "flex flex-col gap-2 rounded-[10px] p-2 transition-transform hover:scale-[1.02]",
                    {
                      "ring-2 ring-white/50": isSelected,
                    }
                  )}
                >
                  <div className="relative flex aspect-video h-16 items-center justify-center rounded bg-[linear-gradient(180deg,#2741E8_0%,#20016B_100%)]">
                    {media.mediaType === MediaType.Video && media.poster ? (
                      <>
                        <ImageWithFallback
                          src={media.poster}
                          alt={media.title}
                          loading="eager"
                          className="h-full w-full rounded object-cover"
                        />
                        <PlayIcon
                          height={16}
                          width={16}
                          className={iconStyles}
                        />
                      </>
                    ) : (
                      <VolumeHighIcon
                        height={16}
                        width={16}
                        className={iconStyles}
                      />
                    )}
                  </div>

                  <div className="flex w-full min-w-0 flex-col items-start">
                    <h3 className="flex w-full min-w-0 gap-1 text-s font-semibold leading-tight dark:text-white">
                      <span className="truncate">{media.title}</span>
                      {isSelected && (
                        <span className="shrink-0">
                          {t("performer:profile.media.indicator.playing")}
                        </span>
                      )}
                      {media.isProcessing && (
                        <span className="shrink-0">
                          {t("performer:profile.media.indicator.processing")}
                        </span>
                      )}
                    </h3>
                    <span className="text-xs dark:text-white/60">
                      {getMediaDuration(media.durationInMs / 1000)}
                    </span>
                  </div>
                </ProfileCard>
              </motion.button>
            );
          })}
        </AnimatePresence>
        {hasHiddenMedia && (
          <motion.button
            layout
            onClick={isExpandedToggle}
            className="col-span-2 mt-1 w-full"
          >
            <ProfileCard className="rounded-[10px] py-2 text-sm uppercase">
              {isExpanded
                ? t("common:button.label.hide")
                : t("common:button.label.show")}
            </ProfileCard>
          </motion.button>
        )}
      </div>
      {fullScreenViewerOpen && (
        <MediaViewer
          toggleFullScreenViewer={toggleFullScreenViewer}
          media={media}
          selectedIndex={selectedMediaIndex}
        />
      )}
    </div>
  );
};

export default MediaSection;
